/**
 * FeatureRequestSubmitPage — multi-step wizard.
 *
 * Steps (per spec):
 *   1. Initial request (plain language)
 *   2. AI interpretation (editable)
 *   3. Follow-up questions (≤3 per round, optional)
 *   4. Review the defined request
 *   5. Demand & user impact
 *   6. Business importance
 *   7. Client importance
 *   8. Mandatory override
 *   9. Evidence + submit
 *
 * Autosaves on every step transition. Original wording never modified.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { formatApiErrorDetail } from "@/lib/api";
import { useFrApi } from "@/lib/frApi";
import {
    Sparkles, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle,
    Loader2, Send, Search, GitMerge, Link2, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = [
    { id: "initial",   label: "Your request",   help: "Describe it in plain language" },
    { id: "ai",        label: "AI clarification", help: "Review AI interpretation + answer follow-ups" },
    { id: "review",    label: "Review",         help: "Confirm the defined request" },
    { id: "demand",    label: "Demand",         help: "Who needs this" },
    { id: "business",  label: "Business",       help: "Why it matters" },
    { id: "client",    label: "Client value",   help: "Importance of requesting clients" },
    { id: "mandatory", label: "Mandatory?",     help: "Compliance, safety, contract" },
    { id: "duplicates", label: "Duplicates",    help: "Check for existing requests" },
    { id: "submit",    label: "Submit",         help: "Confirm + dispatch" },
];

export default function FeatureRequestSubmitPage() {
    const navigate = useNavigate();
    const frApi = useFrApi();
    const isPublic = !!frApi.isPublic;

    // The duplicate-detection step is admin-only — public submissions
    // can't query other tenants' requests for privacy reasons. We hide
    // the step entirely from the public wizard so users don't see a
    // broken button.
    const visibleSteps = isPublic ? STEPS.filter((s) => s.id !== "duplicates") : STEPS;

    const [stepIdx, setStepIdx] = useState(0);
    const [fr, setFr] = useState(null);    // server state
    const [draft, setDraft] = useState({   // local form draft (step 1)
        title: "", description: "", requesterName: "", requesterEmail: "",
        sourceType: isPublic ? "client" : "employee", department: "", clientName: "",
    });
    const [config, setConfig] = useState(null);
    const [aiRunning, setAiRunning] = useState(false);
    const [aiTaskStatus, setAiTaskStatus] = useState(null);
    const [followupRunning, setFollowupRunning] = useState(false);
    const [followupTaskStatus, setFollowupTaskStatus] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        frApi.get("/_/config").then(({ data }) => setConfig(data)).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const step = visibleSteps[stepIdx];
    const fid = fr?.featureRequestId;

    const next = () => setStepIdx((i) => Math.min(visibleSteps.length - 1, i + 1));
    const back = () => setStepIdx((i) => Math.max(0, i - 1));

    // ── Step 1: create the draft on the backend
    const createDraft = async () => {
        if (!draft.description?.trim() || !draft.requesterName?.trim() || !draft.requesterEmail?.trim()) {
            toast.error("Please fill in your name, email, and description.");
            return;
        }
        try {
            // The public endpoint accepts `company` (mapped from clientName)
            // and returns an accessToken we must stash for subsequent calls.
            const body = isPublic
                ? {
                    title:          draft.title,
                    description:    draft.description,
                    requesterName:  draft.requesterName,
                    requesterEmail: draft.requesterEmail,
                    department:     draft.department,
                    company:        draft.clientName,
                    sourceType:     draft.sourceType || "client",
                }
                : draft;
            const { data } = await frApi.post("/drafts", body);
            if (isPublic && data.accessToken) {
                frApi.setAccessToken(data.accessToken);
            }
            setFr(data);
            next();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to start draft");
        }
    };

    // ── Polling helper — used by both AI analyze + follow-up.
    //    The Cloudflare ingress kills any request over 60s, so we
    //    background the LLM call and poll its task status until it
    //    reaches a terminal state.
    const pollAiTask = async (taskId, setStatus) => {
        // Poll once per 2s for up to 5 minutes. The LLM agent itself
        // times out at ~120s — this is the upper bound on retries.
        for (let i = 0; i < 150; i++) {
            await new Promise((r) => setTimeout(r, 2000));
            try {
                const { data } = await frApi.get(`/_/ai-jobs/${taskId}`);
                setStatus(data.status);
                if (data.terminal) {
                    return data;
                }
            } catch {
                /* swallow — transient network blips shouldn't kill the loop */
            }
        }
        return { status: "timeout", terminal: true, error: "Polling exceeded 5 minutes" };
    };

    // ── Step 2 (merged): kick off the AI interpreter, then auto-run
    //    the follow-up question generator. Both background-executed
    //    so the wizard never gets killed by the 60s ingress timeout.
    const runAi = async () => {
        if (!fid) return;
        setAiRunning(true);
        setAiTaskStatus("queued");
        try {
            const { data: enq } = await frApi.post(`/${fid}/ai-analyze`);
            toast.message(enq.coalesced
                ? "AI is already analysing this request — keeping the existing job."
                : "AI is analysing in the background — usually 30-60 seconds, then follow-up questions appear.");
            const result = await pollAiTask(enq.task_id, setAiTaskStatus);
            if (result.status !== "done") {
                toast.error(`AI analyse failed: ${result.error || result.status}`);
                return;
            }
            // Re-fetch draft so the interpretation is visible immediately
            const { data: full } = await frApi.get(`/${fid}`);
            setFr(full);
            toast.success("AI interpretation ready — review below. Generating follow-up questions next…");
            // Immediately chain the follow-up — same step, no extra click
            await runFollowupInline();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "AI failed");
        } finally {
            setAiRunning(false);
        }
    };

    // Internal: drives the follow-up after analyze. Separated from
    // `runFollowup` (kept for the "Ask the next round" button) so the
    // user can re-ask without re-running analyze.
    const runFollowupInline = async () => {
        if (!fid) return;
        setFollowupRunning(true);
        setFollowupTaskStatus("queued");
        try {
            const { data: enq } = await frApi.post(`/${fid}/ai-follow-up`);
            const result = await pollAiTask(enq.task_id, setFollowupTaskStatus);
            if (result.status === "done") {
                const { data: full } = await frApi.get(`/${fid}`);
                setFr(full);
                const latest = (full?.aiAssistance?.followUpHistory || []).slice(-1)[0];
                const qCount = (latest?.questions || []).length;
                if (latest?.done && qCount === 0) {
                    toast.success("AI is satisfied — no follow-up questions needed.");
                } else {
                    toast.success(`AI asked ${qCount} follow-up question${qCount === 1 ? "" : "s"}.`);
                }
            } else {
                // Non-fatal — interpretation is still useful even if
                // follow-up generation hit a 429. User can re-ask later.
                toast.message("Follow-up generation skipped — you can retry from the AI step.");
            }
        } catch {
            toast.message("Follow-up generation skipped — you can retry from the AI step.");
        } finally {
            setFollowupRunning(false);
        }
    };

    // ── Patch helper — every section update goes through this
    const patch = async (body) => {
        if (!fid) return;
        try {
            const { data } = await frApi.patch(`/${fid}`, body);
            setFr(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
        }
    };

    // ── Step 3: get next round of follow-up questions (background-executed)
    //    Used by the "Ask the next round" button inside StepAi.
    const runFollowup = async () => {
        if (!fid) return;
        await runFollowupInline();
    };

    // ── Step 9 (Submit): auto-evaluate when entering so the user sees
    //    the LIVE score before clicking submit. Fixes the production bug
    //    where Submit showed "0/100 Minimal" even with complete answers.
    useEffect(() => {
        if (step?.id !== "submit" || !fid) return;
        let cancelled = false;
        (async () => {
            try {
                const { data: evald } = await frApi.post(`/${fid}/evaluate`);
                if (!cancelled) {
                    setFr((prev) => prev ? { ...prev, evaluation: evald } : prev);
                }
            } catch {
                // Non-fatal — the Submit button will re-evaluate server-side anyway
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step?.id, fid]);

    // ── Step 9: submit
    const submit = async () => {
        if (!fid) return;
        setSubmitting(true);
        try {
            const { data } = await frApi.post(`/${fid}/submit`,
                { confirmAccurate: true });
            setFr(data);
            toast.success("Submitted — your feature request has been dispatched.");
            if (isPublic) {
                // Public users don't have access to the admin detail page —
                // park them on a confirmation route owned by the public layout.
                navigate(`/submit-feature-request/thanks?id=${encodeURIComponent(data.featureRequestId)}`);
            } else {
                navigate(`/feature-requests/${data.featureRequestId}`);
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Submit failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8" data-testid="feature-request-wizard">
            <header>
                <h1 className="font-display text-3xl">Submit a feature request</h1>
                <p className="text-sm text-white/55 mt-1">
                    Plain-language form. The AI will help structure it. You can edit, accept, or reject every suggestion.
                </p>
            </header>

            <ProgressBar stepIdx={stepIdx} steps={visibleSteps} onJump={(i) => fid && setStepIdx(i)} />

            <section className="border border-white/10 rounded-2xl bg-white/[0.02] p-6"
                     data-testid={`step-${step.id}`}>
                <div className="text-xs uppercase tracking-wider text-violet-300 mb-1">
                    Step {stepIdx + 1} of {visibleSteps.length}
                </div>
                <h2 className="font-display text-2xl">{step.label}</h2>
                <div className="text-xs text-white/45 mb-5">{step.help}</div>

                {step.id === "initial" && (
                    <StepInitial draft={draft} setDraft={setDraft} />
                )}
                {step.id === "ai" && (
                    <StepAi fr={fr} patch={patch} runAi={runAi}
                            aiRunning={aiRunning} aiTaskStatus={aiTaskStatus}
                            runFollowup={runFollowup} followupRunning={followupRunning}
                            followupTaskStatus={followupTaskStatus} />
                )}
                {step.id === "review" && (
                    <StepReview fr={fr} patch={patch} />
                )}
                {step.id === "demand" && (
                    <StepDemand fr={fr} patch={patch} config={config} />
                )}
                {step.id === "business" && (
                    <StepBusiness fr={fr} patch={patch} />
                )}
                {step.id === "client" && (
                    <StepClient fr={fr} patch={patch} />
                )}
                {step.id === "mandatory" && (
                    <StepMandatory fr={fr} patch={patch} />
                )}
                {step.id === "duplicates" && (
                    <StepDuplicates fr={fr} setFr={setFr} navigate={navigate} />
                )}
                {step.id === "submit" && (
                    <StepSubmit fr={fr} submit={submit} submitting={submitting} />
                )}
            </section>

            <footer className="flex items-center justify-between">
                {stepIdx > 0 ? (
                    <button onClick={back} data-testid="wizard-back-btn"
                            className="px-3 py-1.5 text-sm border border-white/15 rounded-md hover:bg-white/5 inline-flex items-center gap-1.5">
                        <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                ) : <span />}
                {step.id === "initial" ? (
                    <button onClick={createDraft} data-testid="wizard-start-btn"
                            className="px-4 py-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm rounded-md inline-flex items-center gap-2">
                        Start draft <ArrowRight className="w-4 h-4" />
                    </button>
                ) : step.id !== "submit" ? (
                    <button onClick={next} data-testid="wizard-next-btn"
                            className="px-4 py-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm rounded-md inline-flex items-center gap-2">
                        Continue <ArrowRight className="w-4 h-4" />
                    </button>
                ) : <span />}
            </footer>
        </div>
    );
}


// ============================================================
//  Progress bar
// ============================================================
function ProgressBar({ stepIdx, steps, onJump }) {
    return (
        <ol className="flex items-center gap-1 flex-wrap" data-testid="wizard-progress">
            {steps.map((s, i) => {
                const done = i < stepIdx;
                const active = i === stepIdx;
                return (
                    <li key={s.id}>
                        <button onClick={() => onJump?.(i)} disabled={i > stepIdx}
                                data-testid={`wizard-step-${s.id}`}
                                className={`px-2.5 py-1 text-[10px] uppercase tracking-wider rounded transition-colors ${
                                    active ? "bg-[#7C3AED] text-white"
                                    : done ? "bg-white/10 text-white/65 hover:bg-white/20"
                                    : "bg-transparent text-white/30 cursor-not-allowed"
                                }`}>
                            {i + 1}. {s.label}
                        </button>
                    </li>
                );
            })}
        </ol>
    );
}


// ============================================================
//  Step 1 — initial request
// ============================================================
function StepInitial({ draft, setDraft }) {
    const upd = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
    return (
        <div className="space-y-3">
            <Field label="What would you like the product to do? *"
                   help="Describe it in your own words. It does not need to be technical or perfectly written.">
                <textarea rows={5} value={draft.description} onChange={upd("description")}
                          data-testid="ini-description"
                          placeholder="e.g. We need a way to copy a study schedule between locations — recreating each visit by hand is tedious and error-prone."
                          className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="A short title (optional)" help="If blank, the AI will suggest one.">
                    <input value={draft.title} onChange={upd("title")} data-testid="ini-title"
                           className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                </Field>
                <Field label="Where did this come from?">
                    <select value={draft.sourceType} onChange={upd("sourceType")} data-testid="ini-source"
                            className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm">
                        <option value="employee">Employee</option>
                        <option value="client">Client</option>
                        <option value="prospect">Prospect</option>
                        <option value="support_ticket">Support ticket</option>
                        <option value="implementation">Implementation</option>
                        <option value="sales_opportunity">Sales opportunity</option>
                        <option value="compliance_or_quality_review">Compliance/quality review</option>
                        <option value="other">Other</option>
                    </select>
                </Field>
                <Field label="Your name *">
                    <input value={draft.requesterName} onChange={upd("requesterName")} data-testid="ini-name"
                           className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                </Field>
                <Field label="Your email *">
                    <input type="email" value={draft.requesterEmail} onChange={upd("requesterEmail")} data-testid="ini-email"
                           className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                </Field>
                <Field label="Your department / team">
                    <input value={draft.department} onChange={upd("department")} data-testid="ini-department"
                           className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                </Field>
                <Field label="Client / account (if applicable)">
                    <input value={draft.clientName} onChange={upd("clientName")} data-testid="ini-client"
                           className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                </Field>
            </div>
        </div>
    );
}


// ============================================================
//  Step 2 — AI interpretation
// ============================================================
function StepAi({ fr, patch, runAi, aiRunning, aiTaskStatus,
                  runFollowup, followupRunning, followupTaskStatus }) {
    const ai = fr?.aiAssistance || {};
    const interp = ai.rawInterpretation;
    if (!interp) {
        return (
            <div className="space-y-3">
                <div className="text-sm text-white/65">
                    The AI will read your request, separate the problem from the proposed solution, and produce a structured draft —
                    a title, summary, problem statement, user stories, acceptance criteria, scope, risks. Right after that, it
                    will ask the most useful follow-up questions <strong>in the same step</strong> so you can clarify it in one place.
                </div>
                <div className="text-xs text-white/45 border border-white/10 rounded-lg p-3 bg-white/[0.02]">
                    <div className="font-medium text-white/70 mb-1">Your original wording is preserved.</div>
                    The AI writes into a <em>separate</em> &ldquo;defined request&rdquo; block — your original text is never modified or thrown away.
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={runAi} disabled={aiRunning} data-testid="ai-run-btn"
                            className="px-4 py-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm rounded-md inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                        {aiRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        Help me define this request
                    </button>
                    {aiRunning ? (
                        <AiProgress status={aiTaskStatus} testId="ai-progress" />
                    ) : null}
                </div>
            </div>
        );
    }
    return (
        <div className="space-y-5">
            <ConfidencePill confidence={ai.overallConfidence} />
            <Field label="Suggested title"
                   help={`AI confidence: ${(interp.suggestedTitle?.confidence) || "n/a"}.`}>
                <input value={fr.definedRequest?.approvedTitle || ""}
                       onChange={(e) => patch({ definedRequest: { approvedTitle: e.target.value } })}
                       data-testid="ai-title"
                       className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
            </Field>
            <Field label="Summary" help="Plain-language version of the request.">
                <textarea rows={2} value={fr.definedRequest?.summary || ""}
                          onChange={(e) => patch({ definedRequest: { summary: e.target.value } })}
                          data-testid="ai-summary"
                          className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
            </Field>
            <Field label="Problem statement">
                <textarea rows={3} value={fr.definedRequest?.problemStatement || ""}
                          onChange={(e) => patch({ definedRequest: { problemStatement: e.target.value } })}
                          data-testid="ai-problem"
                          className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
            </Field>
            <Field label="Desired outcome">
                <textarea rows={2} value={fr.definedRequest?.desiredOutcome || ""}
                          onChange={(e) => patch({ definedRequest: { desiredOutcome: e.target.value } })}
                          data-testid="ai-desired"
                          className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
            </Field>
            <div className="flex items-center gap-3">
                <button onClick={runAi} disabled={aiRunning} data-testid="ai-regenerate-btn"
                        className="px-3 py-1.5 text-xs border border-violet-300/30 text-violet-200 rounded-md hover:bg-violet-300/10 inline-flex items-center gap-1.5">
                    {aiRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    Regenerate interpretation
                </button>
                {aiRunning ? (
                    <AiProgress status={aiTaskStatus} testId="ai-progress" />
                ) : null}
            </div>

            {/* ── Inline follow-up questions section ───────────────────────
                Per user request, follow-up questions appear in the SAME step
                as the AI clarification. Auto-triggered after analyze completes.
            ─────────────────────────────────────────────────────────── */}
            <div className="border-t border-white/10 pt-5 mt-2">
                <h3 className="text-base font-display mb-1">Follow-up questions</h3>
                <p className="text-xs text-white/55 mb-3">
                    The AI asks the most useful questions to fully define your request — usually 3-8 per round.
                    Answer what you can; mark &ldquo;Not sure&rdquo; where uncertain. Unanswered items become open questions for product review.
                </p>
                <InlineFollowup fr={fr} patch={patch} runFollowup={runFollowup}
                                running={followupRunning} taskStatus={followupTaskStatus} />
            </div>
        </div>
    );
}


/**
 * InlineFollowup — the follow-up question UI as embedded inside StepAi.
 * Same questions/answers state model as the prior standalone StepFollowup,
 * but tighter chrome so it fits below the interpretation editor.
 */
function InlineFollowup({ fr, patch, runFollowup, running, taskStatus }) {
    const history = fr?.aiAssistance?.followUpHistory || [];
    const latestRound = history[history.length - 1];
    const answers = fr?.aiAssistance?.answers || {};
    const lastDone = latestRound?.done && (latestRound.questions || []).length === 0;

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
                <button onClick={runFollowup} disabled={running} data-testid="followup-run-btn"
                        className="px-3 py-1.5 text-xs bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-md inline-flex items-center gap-1.5 disabled:opacity-50">
                    {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {history.length === 0 ? "Ask follow-up questions" : "Ask another round"}
                </button>
                {running ? <AiProgress status={taskStatus} testId="followup-progress" /> : null}
                {lastDone ? (
                    <span className="text-[11px] text-emerald-200 inline-flex items-center gap-1"
                          data-testid="followup-done">
                        <CheckCircle2 className="w-3 h-3" /> AI is satisfied — no more questions needed.
                    </span>
                ) : null}
            </div>
            <div className="space-y-3">
                {history.map((round, idx) => (
                    <RoundCard key={idx} round={round} idx={idx} answers={answers} patch={patch} />
                ))}
                {history.length === 0 && !running ? (
                    <div className="text-[11px] text-white/40 italic">
                        No follow-up round yet. Once you run the AI above, questions will appear here automatically.
                    </div>
                ) : null}
            </div>
        </div>
    );
}


// ============================================================
//  Step 3 — Follow-up questions
//  ----------------------------------------------------------
//  As of the Phase Feature-Requests-4 fix, follow-up questions
//  are rendered INLINE inside StepAi (per user request). The
//  standalone follow-up step has been removed from the wizard.
//  The `RoundCard` component below is still used by InlineFollowup
//  so we keep it.
// ============================================================

function RoundCard({ round, idx, answers, patch }) {
    return (
        <div className="border border-white/10 rounded-lg p-3 bg-white/[0.02] space-y-3"
             data-testid={`followup-round-${idx + 1}`}>
            <div className="text-[10px] uppercase tracking-wide text-white/40">Round {round.round}</div>
            {(round.questions || []).length === 0 ? (
                <div className="text-xs text-white/45 italic">No questions in this round.</div>
            ) : (round.questions || []).map((q) => (
                <div key={q.id} className="space-y-1.5">
                    <div className="text-sm">{q.prompt}</div>
                    {q.why ? <div className="text-[11px] text-white/45">{q.why}</div> : null}
                    {q.answerType === "yes_no_unsure" ? (
                        <div className="flex gap-2">
                            {["Yes", "No", "Not sure"].map((opt) => (
                                <button key={opt}
                                        data-testid={`q-${q.id}-${opt.toLowerCase().replace(/\s/g, "-")}`}
                                        onClick={() => patch({ answers: { [q.id]: opt } })}
                                        className={`px-3 py-1 text-xs rounded border ${
                                            answers[q.id] === opt
                                                ? "border-violet-300 bg-violet-300/15 text-violet-100"
                                                : "border-white/15 hover:bg-white/5"
                                        }`}>
                                    {opt}
                                </button>
                            ))}
                        </div>
                    ) : q.answerType === "single_select" || q.answerType === "multi_select" ? (
                        <select value={answers[q.id] || ""}
                                onChange={(e) => patch({ answers: { [q.id]: e.target.value } })}
                                data-testid={`q-${q.id}-select`}
                                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm">
                            <option value="">— select —</option>
                            {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                    ) : (
                        <input value={answers[q.id] || ""}
                               onChange={(e) => patch({ answers: { [q.id]: e.target.value } })}
                               data-testid={`q-${q.id}-text`}
                               className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                    )}
                </div>
            ))}
        </div>
    );
}


// ============================================================
//  Step 4 — Review the defined request
// ============================================================
function StepReview({ fr, patch }) {
    const d = fr?.definedRequest || {};
    const updList = (key) => (newList) => patch({ definedRequest: { [key]: newList } });

    return (
        <div className="space-y-4 text-sm">
            <div className="text-xs text-white/55">
                Final pass — edit anything that doesn&apos;t capture the need exactly.
            </div>
            <ListEditor label="In scope" items={d.inScope} setItems={updList("inScope")} testId="rv-inscope" />
            <ListEditor label="Out of scope" items={d.outOfScope} setItems={updList("outOfScope")} testId="rv-outofscope" />
            <ListEditor label="Assumptions" items={d.assumptions} setItems={updList("assumptions")} testId="rv-assumptions" />
            <ListEditor label="Open questions" items={d.openQuestions} setItems={updList("openQuestions")} testId="rv-questions" />
            <div className="border border-white/10 rounded-md p-3 bg-white/[0.02]">
                <div className="text-xs text-white/50 mb-1">User stories (AI-generated)</div>
                <ul className="space-y-1 text-xs">
                    {(d.userStories || []).map((s, i) => (
                        <li key={i}>· As a <strong>{s.role}</strong>, I want to {s.action}, so that {s.benefit}.</li>
                    ))}
                    {(d.userStories || []).length === 0 ? <li className="text-white/40 italic">none</li> : null}
                </ul>
            </div>
            <div className="border border-white/10 rounded-md p-3 bg-white/[0.02]">
                <div className="text-xs text-white/50 mb-1">Acceptance criteria (AI-generated)</div>
                <ul className="space-y-1 text-xs">
                    {(d.acceptanceCriteria || []).map((c, i) => (
                        <li key={i}>· Given {c.given}; when {c.when}; then {c.then}.</li>
                    ))}
                    {(d.acceptanceCriteria || []).length === 0 ? <li className="text-white/40 italic">none</li> : null}
                </ul>
            </div>
        </div>
    );
}

function ListEditor({ label, items, setItems, testId }) {
    const [input, setInput] = useState("");
    const arr = items || [];
    const add = () => { if (input.trim()) { setItems([...arr, input.trim()]); setInput(""); } };
    const remove = (i) => setItems(arr.filter((_, idx) => idx !== i));
    return (
        <div data-testid={testId}>
            <div className="text-xs text-white/55 mb-1.5">{label}</div>
            <ul className="space-y-1 mb-2">
                {arr.map((it, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs">
                        <span className="flex-1">· {typeof it === "string" ? it : JSON.stringify(it)}</span>
                        <button onClick={() => remove(i)} className="text-rose-300 text-[10px] hover:underline">remove</button>
                    </li>
                ))}
            </ul>
            <div className="flex gap-1.5">
                <input value={input} onChange={(e) => setInput(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
                       data-testid={`${testId}-input`}
                       placeholder="add another…"
                       className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-xs" />
                <button onClick={add} data-testid={`${testId}-add`}
                        className="px-3 py-1.5 text-xs border border-white/15 rounded-md hover:bg-white/5">Add</button>
            </div>
        </div>
    );
}


// ============================================================
//  Step 5 — Demand & user impact
// ============================================================
function StepDemand({ fr, patch, config }) {
    const d = fr?.demand || {};
    const u = fr?.userImpact || {};
    return (
        <div className="space-y-4 text-sm">
            <BucketSelect label="How many different clients have asked for this?"
                          value={d.clientDemandBucket}
                          options={[["none", "No client has asked yet"], ["one", "One client"],
                                    ["two_to_three", "2–3 clients"], ["four_to_five", "4–5 clients"],
                                    ["six_to_ten", "6–10 clients"], ["more_than_10", "More than 10"],
                                    ["unknown", "I do not know"]]}
                          map={config?.buckets?.clientDemand}
                          onChange={(v, num) => patch({ demand: { clientDemandBucket: v, uniqueRequestingClients: num } })}
                          testId="dm-clientdemand" />
            <NumField label="Total active clients (used in scoring)" value={d.totalActiveClients}
                      onChange={(n) => patch({ demand: { totalActiveClients: n } })} testId="dm-totalclients" />
            <BucketSelect label="How many people would benefit from this?"
                          value={u.userReachBucket}
                          options={[["small_group", "A small group"], ["some_users", "Some users"],
                                    ["many_users", "Many users"], ["most_relevant", "Most relevant users"],
                                    ["nearly_every_relevant", "Nearly every relevant user"],
                                    ["unknown", "I do not know"]]}
                          map={config?.buckets?.userReach}
                          onChange={(v, num) => patch({ userImpact: { userReachBucket: v, affectedUserShare: num } })}
                          testId="dm-userreach" />
            <BucketSelect label="About how much time would this save each time it is used?"
                          value={u.timeSavedBucket}
                          options={[["none", "It doesn't primarily save time"],
                                    ["lt_2_min", "Less than 2 minutes"], ["about_5_min", "About 5 minutes"],
                                    ["about_10_min", "About 10 minutes"], ["about_15_min", "About 15 minutes"],
                                    ["about_30_min", "About 30 minutes"], ["about_1_hour", "About 1 hour"],
                                    ["more_than_1_hr", "More than 1 hour"], ["unknown", "I do not know"]]}
                          map={config?.buckets?.timeSavedMinutes}
                          onChange={(v, num) => patch({ userImpact: { timeSavedBucket: v, medianMinutesSavedPerUse: num } })}
                          testId="dm-timesaved" />
            <BucketSelect label="How often would a typical person use this feature?"
                          value={u.frequencyBucket}
                          options={[["lt_yearly", "Less than once a year"],
                                    ["few_per_year", "A few times a year"],
                                    ["monthly", "About once a month"],
                                    ["few_per_month", "A few times a month"],
                                    ["weekly", "About once a week"],
                                    ["few_per_week", "Several times a week"],
                                    ["daily", "About once each workday"],
                                    ["several_per_day", "Several times a day"],
                                    ["unknown", "I do not know"]]}
                          map={config?.buckets?.frequencyPerMonth}
                          onChange={(v, num) => patch({ userImpact: { frequencyBucket: v, expectedUsesPerUserPerMonth: num } })}
                          testId="dm-frequency" />
        </div>
    );
}


// ============================================================
//  Step 6 — Business importance
// ============================================================
const BIZ_OPTS_4 = [
    "No known effect", "Possibly — no evidence yet", "May help some opportunities",
    "Likely to affect meaningful revenue/retention", "Directly connected to an important deal or churn risk",
];

function StepBusiness({ fr, patch }) {
    const b = fr?.businessImportance || {};
    const upd = (k) => (n) => patch({ businessImportance: { [k]: n } });
    return (
        <div className="space-y-4 text-sm">
            <RadioRow label="Could this help win new business, expand an account, complete a renewal, or prevent churn?"
                      value={b.revenueAndRetention} onChange={upd("revenueAndRetention")}
                      options={BIZ_OPTS_4} testId="biz-revenue" />
            <RadioRow label="Could this reduce legal, regulatory, security, privacy, quality, audit, or data-integrity risk?"
                      value={b.riskAndCompliance} onChange={upd("riskAndCompliance")}
                      options={[
                          "No known effect", "Small improvement",
                          "Meaningful control or audit improvement",
                          "Important risk reduction", "Critical requirement or known serious risk",
                      ]} testId="biz-risk" />
            <RadioRow label="How closely does this fit the direction of the product?"
                      value={b.strategicAlignment} onChange={upd("strategicAlignment")}
                      options={[
                          "Outside the current direction", "Loosely related",
                          "Fits the product", "Strongly supports a current strategic priority",
                          "Central to the product direction",
                      ]} testId="biz-strategy" />
            <RadioRow label="Would this reduce support work, setup time, rework, implementation work, or recurring effort?"
                      value={b.internalOperationalValue} onChange={upd("internalOperationalValue")}
                      options={[
                          "No known saving", "Small saving", "Noticeable saving",
                          "Large saving across a team or process",
                          "Major recurring cost or workload reduction",
                      ]} testId="biz-internal" />
        </div>
    );
}

function RadioRow({ label, value, onChange, options, testId }) {
    return (
        <div data-testid={testId}>
            <div className="text-xs text-white/55 mb-1.5">{label}</div>
            <div className="flex flex-wrap gap-1.5">
                {options.map((opt, i) => (
                    <button key={i} onClick={() => onChange(i)}
                            data-testid={`${testId}-${i}`}
                            className={`px-2.5 py-1 text-[11px] rounded border transition-colors ${
                                value === i
                                    ? "border-violet-300 bg-violet-300/15 text-violet-100"
                                    : "border-white/15 hover:bg-white/5"
                            }`}>
                        {opt}
                    </button>
                ))}
            </div>
        </div>
    );
}


// ============================================================
//  Step 7 — Client importance
// ============================================================
function StepClient({ fr, patch }) {
    const c = fr?.clientImportance || {};
    return (
        <div className="space-y-4 text-sm">
            <BucketSelect label="Overall, how important are the clients requesting this?"
                          value={c.averageBucket}
                          options={[["unknown", "Not yet known"],
                                    ["lower_value", "Lower-value or limited-use clients"],
                                    ["typical", "Typical clients"],
                                    ["important", "Important clients with meaningful usage/revenue"],
                                    ["key_strategic", "Mostly key or strategic clients"]]}
                          map={{ unknown: 0, lower_value: 0.25, typical: 0.5, important: 0.7, key_strategic: 0.9 }}
                          onChange={(v, num) => patch({ clientImportance: { averageBucket: v, average: num } })}
                          testId="cli-avg" />
            <BucketSelect label="Is any one requesting client especially important?"
                          value={c.keyClientBucket}
                          options={[["no", "No / not known"], ["somewhat", "Somewhat important"],
                                    ["important", "Important"], ["key", "Key account"],
                                    ["strategic", "Strategic account or serious renewal risk"]]}
                          map={{ no: 0, somewhat: 0.4, important: 0.65, key: 0.85, strategic: 1.0 }}
                          onChange={(v, num) => patch({ clientImportance: { keyClientBucket: v, maximum: num } })}
                          testId="cli-key" />
            <div className="text-xs text-white/40">
                Your answer is treated as an <em>estimate</em>. Authorized product users can correct it later
                with CRM data.
            </div>
        </div>
    );
}


// ============================================================
//  Step 8 — Mandatory override
// ============================================================
function StepMandatory({ fr, patch }) {
    const m = fr?.mandatoryOverride || {};
    return (
        <div className="space-y-3 text-sm">
            <label className="flex items-start gap-2.5 cursor-pointer p-3 border border-white/10 rounded-md hover:bg-white/[0.03]">
                <input type="checkbox" checked={!!m.enabled}
                       onChange={(e) => patch({ mandatoryOverride: { enabled: e.target.checked,
                                                                       confirmedAt: e.target.checked ? new Date().toISOString() : null } })}
                       data-testid="mand-toggle"
                       className="mt-1" />
                <span>
                    <span className="block">
                        This request is required because of a law, regulation, contract, patient safety,
                        security, data-integrity, or service-continuity issue.
                    </span>
                    <span className="block text-[11px] text-white/45 mt-1">
                        Choosing this flags the request as <strong>MANDATORY</strong> regardless of the calculated score.
                        Only an authorized product reviewer can confirm — your selection is an estimate.
                    </span>
                </span>
            </label>
            {m.enabled ? (
                <>
                    <Field label="Reason *">
                        <select value={m.type || ""}
                                onChange={(e) => patch({ mandatoryOverride: { type: e.target.value } })}
                                data-testid="mand-type"
                                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm">
                            <option value="">— select —</option>
                            <option value="legal_regulatory">Legal or regulatory requirement</option>
                            <option value="contractual">Contractual obligation</option>
                            <option value="patient_safety">Patient safety</option>
                            <option value="security_privacy">Security or privacy risk</option>
                            <option value="data_integrity">Data integrity or auditability</option>
                            <option value="service_availability">Service availability</option>
                            <option value="business_continuity">Business continuity</option>
                            <option value="other">Other critical obligation</option>
                        </select>
                    </Field>
                    <Field label="Explanation *">
                        <textarea rows={3} value={m.details || ""}
                                  onChange={(e) => patch({ mandatoryOverride: { details: e.target.value } })}
                                  data-testid="mand-details"
                                  className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                    </Field>
                    <Field label="Deadline (optional)">
                        <input type="date" value={m.deadline || ""}
                               onChange={(e) => patch({ mandatoryOverride: { deadline: e.target.value } })}
                               data-testid="mand-deadline"
                               className="bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                    </Field>
                </>
            ) : null}
        </div>
    );
}


// ============================================================
//  Step 9 — Duplicates (per spec section 13)
// ============================================================
function StepDuplicates({ fr, setFr, navigate }) {
    const frApi = useFrApi();
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);   // {matches, shortlist_size, phase, fallback?}
    const [actionBusy, setActionBusy] = useState(null);   // 'merge:<id>' | 'link:<id>' | 'cancel'

    const fid = fr?.featureRequestId;

    const runCheck = async () => {
        if (!fid) return;
        setRunning(true);
        setResult(null);
        try {
            const { data } = await frApi.post(`/${fid}/check-duplicates`);
            setResult(data);
            if ((data.matches || []).length === 0) {
                toast.success(
                    data.shortlist_size > 0
                        ? "No close matches — your request looks new."
                        : "No existing requests to compare against — you're the first."
                );
            } else {
                toast.message(`Found ${data.matches.length} possible duplicate${data.matches.length === 1 ? "" : "s"} — review below.`);
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Duplicate check failed");
        } finally {
            setRunning(false);
        }
    };

    const addEvidence = async (existingId) => {
        setActionBusy(`merge:${existingId}`);
        try {
            const { data } = await frApi.post(`/${fid}/add-evidence/${existingId}`);
            toast.success(
                `Added your client + evidence to the existing request. New score: ${
                    data?.existing?.evaluation?.score ?? "—"
                }`
            );
            // The draft is now marked merged_into — navigate the user to the merged target
            navigate(`/feature-requests/${existingId}`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Add evidence failed");
        } finally {
            setActionBusy(null);
        }
    };

    const linkRelated = async (existingId) => {
        setActionBusy(`link:${existingId}`);
        try {
            await frApi.post(`/${fid}/link-related/${existingId}`);
            toast.success("Linked as related — both records now reference each other.");
            // Re-pull the draft so relatedRequestIds is current
            const { data } = await frApi.get(`/${fid}`);
            setFr(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Link failed");
        } finally {
            setActionBusy(null);
        }
    };

    const matches = result?.matches || [];
    const linked = new Set(fr?.relatedRequestIds || []);

    return (
        <div className="space-y-4" data-testid="step-duplicates">
            <div className="text-sm text-white/65">
                Before submitting, we&apos;ll look for existing feature requests that describe the same
                underlying need. You can <strong>add your client and evidence to an existing one</strong>{" "}
                (so your voice raises its score and YouTrack/Monday records get updated), or
                <strong> link the requests as related</strong> if they touch overlapping areas.
            </div>

            <button onClick={runCheck} disabled={running || !!actionBusy}
                    data-testid="duplicates-run-btn"
                    className="px-4 py-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm rounded-md inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {result ? "Re-run check" : "Check for duplicates"}
            </button>

            {result?.fallback ? (
                <div className="text-[11px] text-amber-200 border border-amber-300/30 bg-amber-300/5 rounded-md px-3 py-2 inline-flex items-center gap-1.5"
                     data-testid="duplicates-fallback-banner">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    AI scoring unavailable right now — showing keyword-overlap results only. Refresh to retry.
                </div>
            ) : null}

            {result && matches.length === 0 ? (
                <div className="border border-emerald-300/30 bg-emerald-300/5 rounded-lg p-4 text-sm text-emerald-100 inline-flex items-center gap-2"
                     data-testid="duplicates-none-found">
                    <CheckCircle2 className="w-4 h-4" />
                    No close matches found in {result.shortlist_size} candidate{result.shortlist_size === 1 ? "" : "s"}.
                    You can continue and submit.
                </div>
            ) : null}

            {matches.length > 0 ? (
                <ul className="space-y-3" data-testid="duplicates-matches-list">
                    {matches.map((m) => (
                        <MatchCard
                            key={m.existing_id}
                            match={m}
                            already_linked={linked.has(m.existing_id)}
                            on_add_evidence={() => addEvidence(m.existing_id)}
                            on_link_related={() => linkRelated(m.existing_id)}
                            actionBusy={actionBusy}
                        />
                    ))}
                </ul>
            ) : null}

            {result && matches.length > 0 ? (
                <div className="text-[11px] text-white/45">
                    Don&apos;t see your request here? You can ignore the matches and continue to the
                    Submit step — a fresh feature request will be created.
                </div>
            ) : null}
        </div>
    );
}


function MatchCard({ match, already_linked, on_add_evidence, on_link_related, actionBusy }) {
    const id = match.existing_id;
    const rec = match.recommendation;
    const recCls = rec === "same_need"
        ? "border-rose-300/40 bg-rose-300/5 text-rose-100"
        : rec === "related"
            ? "border-amber-300/40 bg-amber-300/5 text-amber-100"
            : "border-white/15 bg-white/[0.02] text-white/70";
    const recLabel = rec === "same_need" ? "Same need" : rec === "related" ? "Related" : "Unrelated";
    const confLabel = `${(match.confidence || "medium")} confidence`;
    const isBusyMerge = actionBusy === `merge:${id}`;
    const isBusyLink = actionBusy === `link:${id}`;

    return (
        <li className={`border rounded-xl p-4 ${recCls}`} data-testid={`duplicate-match-${id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] px-2 py-0.5 rounded border border-current/30">
                            {recLabel} · {Math.round((match.similarity || 0) * 100)}%
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-white/60">
                            {confLabel}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-white/60">
                            {match.existing_classification || "—"}
                        </span>
                        {already_linked ? (
                            <span className="text-[10px] px-2 py-0.5 rounded border border-violet-300/30 bg-violet-300/10 text-violet-100 inline-flex items-center gap-1">
                                <Link2 className="w-3 h-3" /> linked
                            </span>
                        ) : null}
                    </div>
                    <Link to={`/feature-requests/${id}`}
                          className="text-sm font-medium hover:underline truncate block"
                          data-testid={`duplicate-match-${id}-title`}>
                        {match.existing_title}
                    </Link>
                    <div className="text-xs mt-1.5 italic">{match.explanation}</div>
                    <div className="text-[11px] mt-2 text-white/45 flex gap-3 flex-wrap">
                        <span>Score {match.existing_score}/100</span>
                        <span>Requesting clients: {match.requesting_clients}</span>
                        {match.youtrack_url ? (
                            <a href={match.youtrack_url} target="_blank" rel="noopener noreferrer"
                               className="inline-flex items-center gap-0.5 hover:underline">
                                YouTrack <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                        ) : null}
                        {match.monday_url ? (
                            <a href={match.monday_url} target="_blank" rel="noopener noreferrer"
                               className="inline-flex items-center gap-0.5 hover:underline">
                                Monday <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                        ) : null}
                    </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                    <button onClick={on_add_evidence} disabled={!!actionBusy}
                            data-testid={`duplicate-match-${id}-merge-btn`}
                            className="text-[11px] px-2.5 py-1.5 border border-current rounded inline-flex items-center gap-1 hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed">
                        {isBusyMerge ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitMerge className="w-3 h-3" />}
                        Add my evidence
                    </button>
                    <button onClick={on_link_related} disabled={!!actionBusy || already_linked}
                            data-testid={`duplicate-match-${id}-link-btn`}
                            className="text-[11px] px-2.5 py-1.5 border border-current/40 rounded inline-flex items-center gap-1 hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed">
                        {isBusyLink ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
                        {already_linked ? "Linked" : "Link as related"}
                    </button>
                </div>
            </div>
        </li>
    );
}


// ============================================================
//  Step 10 — Submit
// ============================================================
function StepSubmit({ fr, submit, submitting }) {
    const ev = fr?.evaluation || {};
    const ev_score = ev.score ?? 0;
    const evaluatedAt = ev.formulaVersion ? new Date(fr?.updatedAt || Date.now()) : null;
    const isMandatory = fr?.mandatoryOverride?.enabled;
    const components = ev.components || {};
    const classifTone = ev_score >= 80 ? "text-rose-300"
                        : ev_score >= 65 ? "text-amber-300"
                        : ev_score >= 50 ? "text-violet-300"
                        : ev_score >= 35 ? "text-sky-300"
                        : "text-white/60";
    return (
        <div className="space-y-5" data-testid="submit-step-content">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="border border-white/10 rounded-lg p-4 text-center"
                     data-testid="submit-score-card">
                    <div className="text-[10px] uppercase text-white/45 tracking-wide">Score</div>
                    <div className={`text-5xl font-display ${classifTone}`} data-testid="submit-score-value">
                        {ev_score}
                    </div>
                    <div className="text-xs text-white/50 mt-1" data-testid="submit-score-classification">
                        {ev.classification || "—"}
                    </div>
                </div>
                <div className="border border-white/10 rounded-lg p-4 text-center">
                    <div className="text-[10px] uppercase text-white/45 tracking-wide">Effective priority</div>
                    <div className={`text-2xl font-display mt-1 ${isMandatory ? "text-rose-300" : ""}`}>
                        {ev.effectivePriorityClass || "—"}
                    </div>
                    {isMandatory ? (
                        <div className="text-[10px] text-rose-300 inline-flex items-center gap-1 mt-1">
                            <AlertTriangle className="w-3 h-3" /> Mandatory override
                        </div>
                    ) : null}
                </div>
                <div className="border border-white/10 rounded-lg p-4 text-center">
                    <div className="text-[10px] uppercase text-white/45 tracking-wide">Formula</div>
                    <div className="text-sm font-mono mt-2">{ev.formulaVersion || "—"}</div>
                    {evaluatedAt ? (
                        <div className="text-[10px] text-white/40 mt-1">live</div>
                    ) : null}
                </div>
            </div>

            {/* ── Score-components breakdown (LIVE — shows the user
                 EXACTLY why their score is what it is, per the
                 production bug fix). ──────────────────────────── */}
            {Object.keys(components).length > 0 ? (
                <div className="border border-white/10 rounded-xl bg-white/[0.02] p-4"
                     data-testid="submit-score-components">
                    <div className="text-xs uppercase tracking-wider text-white/45 mb-3">
                        How your score is calculated
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <ScoreBar label="Client demand"
                                  value={components.clientDemand}
                                  weight={0.25}
                                  description="Reach across clients (proportional + absolute)" />
                        <ScoreBar label="Operational impact"
                                  value={components.operationalImpact}
                                  weight={0.30}
                                  description="Users × time-saved × frequency" />
                        <ScoreBar label="Business importance"
                                  value={components.businessImportance}
                                  weight={0.25}
                                  description="Revenue · risk · strategy · ops" />
                        <ScoreBar label="Client value"
                                  value={components.requestingClientImportance}
                                  weight={0.20}
                                  description="Importance of the requesting clients" />
                    </div>
                    <div className="text-[10px] text-white/40 mt-3 font-mono">
                        Score = 100 × (0.25·D + 0.30·O + 0.25·B + 0.20·I) · then capped 0–100
                    </div>
                </div>
            ) : (
                <div className="border border-amber-300/30 bg-amber-300/5 rounded-lg p-3 text-xs text-amber-100"
                     data-testid="submit-score-evaluating">
                    Calculating your score…
                </div>
            )}

            <div className="border border-amber-300/30 bg-amber-300/5 rounded-lg p-3 text-xs text-amber-100">
                On submit we will create linked records in YouTrack and Monday.com, keyed on this feature
                request&apos;s UUID. The integration is idempotent — accidental re-submits won&apos;t create duplicates.
            </div>
            <button onClick={submit} disabled={submitting} data-testid="wizard-submit-btn"
                    className="px-5 py-2.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-md inline-flex items-center gap-2 disabled:opacity-50">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Submit feature request
            </button>
        </div>
    );
}


function ScoreBar({ label, value, weight, description }) {
    const v = Number(value || 0);
    const pct = Math.round(Math.max(0, Math.min(1, v)) * 100);
    const contribution = (v * weight * 100);
    return (
        <div className="border border-white/10 rounded-md p-2.5 bg-white/[0.02]"
             data-testid={`score-bar-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-baseline justify-between">
                <div className="text-white/65">{label}</div>
                <div className="text-[10px] text-white/40">×{weight.toFixed(2)}</div>
            </div>
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mt-1.5">
                <div className="h-full bg-violet-400 transition-all duration-300"
                     style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-baseline justify-between mt-1">
                <span className="text-white/45 text-[10px]">{description}</span>
                <span className="text-white/80 text-[11px] font-mono">+{contribution.toFixed(1)}</span>
            </div>
        </div>
    );
}


// ============================================================
//  Shared primitives
// ============================================================
function Field({ label, help, children }) {
    return (
        <label className="block">
            <div className="text-xs text-white/55 mb-1">{label}</div>
            {children}
            {help ? <div className="text-[10px] text-white/40 mt-1">{help}</div> : null}
        </label>
    );
}

function NumField({ label, value, onChange, testId }) {
    return (
        <Field label={label}>
            <input type="number" value={value ?? 0}
                   onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
                   data-testid={testId}
                   className="w-32 bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-sm" />
        </Field>
    );
}

function BucketSelect({ label, value, options, map, onChange, testId }) {
    return (
        <div data-testid={testId}>
            <div className="text-xs text-white/55 mb-1.5">{label}</div>
            <div className="flex flex-wrap gap-1.5">
                {options.map(([key, lbl]) => (
                    <button key={key} onClick={() => onChange(key, map?.[key] ?? 0)}
                            data-testid={`${testId}-${key}`}
                            className={`px-2.5 py-1 text-[11px] rounded border transition-colors ${
                                value === key
                                    ? "border-violet-300 bg-violet-300/15 text-violet-100"
                                    : "border-white/15 hover:bg-white/5"
                            }`}>
                        {lbl}
                    </button>
                ))}
            </div>
        </div>
    );
}

function ConfidencePill({ confidence }) {
    const tone = confidence === "high" ? "emerald" : confidence === "low" ? "rose" : "amber";
    const cls = tone === "emerald" ? "border-emerald-300/30 bg-emerald-300/5 text-emerald-200"
              : tone === "rose"   ? "border-rose-300/30 bg-rose-300/5 text-rose-200"
              : "border-amber-300/30 bg-amber-300/5 text-amber-100";
    return (
        <div className={`text-[11px] px-2.5 py-1 inline-flex items-center gap-1.5 rounded border ${cls}`}>
            AI confidence: {confidence || "n/a"}
        </div>
    );
}


/**
 * AiProgress — small live status pill rendered while a background AI
 * task is in flight. The wizard polls `/_/ai-jobs/{task_id}` every 2s
 * and the parent feeds the queue row's `status` here.
 *
 * The LLM agent itself takes 30-120s, so we surface a friendly
 * "queued" → "running" → "done"/"failed" sequence rather than a
 * silent spinner. Helps with the 60s-Cloudflare-timeout narrative —
 * the user knows the request is alive even if it takes a minute.
 */
function AiProgress({ status, testId }) {
    const label = status === "queued"  ? "Waiting for an LLM worker…"
                : status === "running" ? "AI is thinking — usually 30-60s…"
                : status === "done"    ? "Done."
                : status === "dead"    ? "AI gave up after retries."
                : status === "timeout" ? "Timed out waiting."
                : "Working…";
    const tone = status === "dead" || status === "timeout"
        ? "border-rose-300/30 bg-rose-300/5 text-rose-100"
        : status === "done"
            ? "border-emerald-300/30 bg-emerald-300/5 text-emerald-100"
            : "border-violet-300/30 bg-violet-300/5 text-violet-100";
    return (
        <span className={`text-[11px] px-2.5 py-1 rounded border inline-flex items-center gap-1.5 ${tone}`}
              data-testid={testId}>
            <Loader2 className="w-3 h-3 animate-spin" /> {label}
        </span>
    );
}
